import log from './logger/log';

const OFFLINE_RESPONSES_CACHE = 'offlineResponses';
const ONLINE_REQUESTS_CACHE = 'onlineRequests';
const IMPORTANT_REQUESTS_CACHE = 'importantRequests';

const cacheData = async (cacheName, url, data) => {
    try {
        const cache = await caches.open(cacheName);

        await cache.put(url, new Response(JSON.stringify(data)));

        log.debug(`cacheService: Data cached successfully. CacheName: ${cacheName}. Url: ${url}`);
    } catch (e) {
        log.error(`cacheService: Failed to cache data. CacheName: ${cacheName}. Url: ${url}`, e?.message);

        throw e;
    }
};

const getCachedData = async (cacheName, url) => {
    const cache = await caches.open(cacheName);

    try {
        const response = await cache.match(url);

        if (response) {
            const data = await response.json();

            log.debug(`cacheService: Successfully received data. CacheName: ${cacheName}. Url: ${url}`);

            return data;
        } else {
            throw new Error('No cached data found');
        }
    } catch (e) {
        log.error(`cacheService: Error retrieving cached data. CacheName: ${cacheName}. Url: ${url}`, e?.message);

        throw e;
    }
};

const getAllCachedData = async (cacheName) => {
    const cache = await caches.open(cacheName);
    const cacheKeys = await cache.keys();
    const results = [];

    try {
        for (const {url} of cacheKeys) {
            const data = await getCachedData(cacheName, url);

            results.push({url, data});
        }

        return results;
    } catch (e) {
        log.error(`cacheService: Error retrieving cached request. CacheName: ${cacheName}`, e?.message);

        throw e;
    }
};

const deleteCachedData = async (cacheName, url) => {
    const cache = await caches.open(cacheName);

    try {
        await cache.delete(url);

        log.debug(`cacheService: Successfully deleted data. CacheName: ${cacheName}. Url: ${url}`);
    } catch (e) {
        log.error(`cacheService: Failed to delete data. CacheName: ${cacheName}. Url: ${url}`, e?.message);

        throw e;
    }
};

const clearCaches = async () => {
    try {
        const cacheNames = [OFFLINE_RESPONSES_CACHE, ONLINE_REQUESTS_CACHE, IMPORTANT_REQUESTS_CACHE];

        await Promise.all(cacheNames.map((cacheName) => caches.delete(cacheName)));

        log.debug('cacheService: Caches deleted successfully');
    } catch (e) {
        log.error('cacheService: Failed to delete caches:', e?.message);
    }
};

const cacheResponse = (url, data) => cacheData(OFFLINE_RESPONSES_CACHE, url, data);

const cacheRequest = (url, data) => cacheData(ONLINE_REQUESTS_CACHE, url, data);

const cacheImportantRequest = (url, data) => cacheData(IMPORTANT_REQUESTS_CACHE, url, data);

const getCachedResponse = (url) => getCachedData(OFFLINE_RESPONSES_CACHE, url);

const getCachedOnlineRequests = () => getAllCachedData(ONLINE_REQUESTS_CACHE);

const getCachedImportantRequests = () => getAllCachedData(IMPORTANT_REQUESTS_CACHE);

const deleteCachedRequest = (url) => deleteCachedData(ONLINE_REQUESTS_CACHE, url);

const deleteCachedImportantRequest = (url) => deleteCachedData(IMPORTANT_REQUESTS_CACHE, url);

export default {
    cacheData,
    getCachedData,
    deleteCachedData,
    clearCaches,
    cacheResponse,
    cacheRequest,
    getCachedResponse,
    getCachedOnlineRequests,
    deleteCachedRequest,
    cacheImportantRequest,
    getCachedImportantRequests,
    deleteCachedImportantRequest,
};
